import { useState, useEffect } from "react";
import { voteOnPoll } from "../api/polls";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

const VoteModal = ({
  isOpen,
  onClose,
  poll,
  onVoteSuccess,
  votedPolls,
  setVotedPolls,
}) => {
  const { t } = useTranslation();
  const [selectedOption, setSelectedOption] = useState([]);
  const [voteLoading, setVoteLoading] = useState(false);
  const [voteError, setVoteError] = useState("");

  // Expiry helper
  const isExpired = (expiry_datetime) => {
    if (!expiry_datetime) return false;
    return new Date(expiry_datetime) < new Date();
  };

  // Initialize selected options when modal opens
  useEffect(() => {
    if (isOpen && poll) {
      const votedOptions = Array.isArray(poll.poll_options)
        ? poll.poll_options
            .filter((opt) => opt.is_selected)
            .map((opt) => opt.id)
        : [];
      setSelectedOption(votedOptions);
      setVoteError("");
    }
  }, [isOpen, poll]);

  const handleVoteSubmit = () => {
    if (!Array.isArray(selectedOption) || selectedOption.length === 0) {
      setVoteError("Please select at least one option");
      return;
    }

    setVoteLoading(true);
    setVoteError("");

    voteOnPoll(poll.id, JSON.stringify(selectedOption))
      .then((response) => {
        setVotedPolls((prev) => [...prev, poll.id]);
        toast.success("Vote submitted successfully!");
        setVoteError("");

        // Get updated poll options from response
        const updatedOptions = response?.data?.data?.poll?.poll_options
          ? response.data.data.poll.poll_options
          : poll.poll_options;

        // Call the parent's success handler with updated poll
        const updatedPoll = {
          ...poll,
          is_voted: true,
          poll_options: updatedOptions,
        };

        onVoteSuccess(updatedPoll);
        onClose();
      })
      .catch((err) => {
        setVoteError(err?.message || "Failed to submit vote");
      })
      .finally(() => setVoteLoading(false));
  };

  const handleOptionChange = (optionId, checked) => {
    if (poll.is_voted || isExpired(poll.expiry_datetime)) return;

    const selectionLimit = Number(poll.selection) || 1;

    if (!Array.isArray(selectedOption)) {
      setSelectedOption(checked ? [optionId] : []);
    } else {
      if (checked) {
        if (selectionLimit === 1) {
          setSelectedOption([optionId]);
        } else {
          setSelectedOption([...selectedOption, optionId]);
        }
      } else {
        setSelectedOption(selectedOption.filter((id) => id !== optionId));
      }
    }
  };

  if (!isOpen || !poll) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 px-4">
      <div
        className="bg-white dark:bg-[#252739] w-full max-w-md p-0 rounded-2xl shadow-lg relative transition-colors duration-200 vote-modal-card"
        style={{ maxHeight: "80vh", overflowY: "auto" }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 text-red-600 dark:text-red-400 text-2xl font-bold z-10"
          style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}
          aria-label="Close"
          disabled={voteLoading}
        >
          &times;
        </button>

        <div className="flex flex-col items-center px-6 pt-8 pb-6">
          <h2 className="text-2xl font-bold text-center text-gray-900 dark:text-dark-text mb-2">
            {t("time_to_vote")}
          </h2>
          <h3 className="text-lg font-semibold text-center mb-2 text-gray-900 dark:text-dark-text">
            {poll.poll_title || poll.question || "Poll"}
          </h3>

          {/* Expiry date and time */}
          {poll.expiry_datetime && (
            <div className="text-gray-500 dark:text-dark-text-secondary text-sm mb-4 text-center">
              {t("ends")}{" "}
              {(() => {
                const utcDate = new Date(poll.expiry_datetime + "Z");
                return utcDate.toLocaleString("en-US", {
                  month: "2-digit",
                  day: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                });
              })()}
            </div>
          )}

          <div
            className="w-full space-y-4"
            style={{ maxHeight: "50vh", overflowY: "auto" }}
          >
            {voteError && (
              <div className="text-red-500 text-sm mb-2 text-center">
                {voteError}
              </div>
            )}

            {Array.isArray(poll.poll_options) &&
            poll.poll_options.length > 0 ? (
              poll.poll_options.map((opt) => {
                // Calculate percentage if already voted
                let percentage = null;
                if (poll.is_voted || votedPolls.includes(poll.id)) {
                  const totalVotes = poll.poll_options.reduce(
                    (sum, o) => sum + (o.vote_count || 0),
                    0
                  );
                  percentage =
                    totalVotes > 0
                      ? ((opt.vote_count / totalVotes) * 100).toFixed(2)
                      : "0.0";
                }

                // Check if option is selected
                const isChecked = poll.is_voted
                  ? !!opt.is_selected
                  : Array.isArray(selectedOption) &&
                    selectedOption.includes(opt.id);

                return (
                  <label
                    key={opt.id}
                    className={`flex items-center gap-3 border border-gray-200 dark:border-dark-border rounded-lg px-4 py-4 transition-all duration-150 cursor-pointer shadow-sm vote-option-card bg-white dark:bg-[#252739]-light ${
                      isChecked ? "is-selected" : ""
                    }`}
                  >
                    {/* <input
                      type="checkbox"
                      name="voteOption"
                      value={opt.id}
                      checked={isChecked}
                      onChange={(e) =>
                        handleOptionChange(opt.id, e.target.checked)
                      }
                      className="w-6 h-6 accent-[#6d29f6] rounded focus:ring-0 focus:ring-[#6d29f6]"
                      style={{ accentColor: "#6d29f6" }}
                    /> */}

                    <label className="relative flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        name="voteOption"
                        value={opt.id}
                        checked={isChecked}
                        onChange={(e) =>
                          handleOptionChange(opt.id, e.target.checked)
                        }
                        className="peer sr-only"
                      />

                      <div
                        className={`h-5 w-5 border-2 rounded-md flex items-center justify-center 
                  transition-all duration-200 ${
                    isChecked
                      ? "bg-[#6d29f6] border-[#6d29f6]"
                      : "bg-transparent border-[#6d29f6]"
                  }`}
                      >
                        {isChecked && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="white"
                            className="w-3 h-3"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-7.414 7.414a1 1 0 01-1.414 0L3.293 9.414a1 1 0 011.414-1.414L8.5 11.793l6.793-6.793a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>
                    </label>

                    <span className="flex-1 text-gray-800 dark:text-dark-text font-medium text-base">
                      {opt.option}
                    </span>

                    {/* Show percentage and vote count if already voted */}
                    {(poll.is_voted || votedPolls.includes(poll.id)) && (
                      <span className="flex flex-col items-end ml-2">
                        <span
                          className="text-sm font-bold"
                          style={{ color: "#6d29f6" }}
                        >
                          {percentage}%
                        </span>
                        <span
                          className="text-sm font-bold"
                          style={{ color: "#6d29f6" }}
                        >
                          {opt.vote_count} Votes
                        </span>
                      </span>
                    )}
                  </label>
                );
              })
            ) : (
              <div className="text-gray-500 dark:text-dark-text-secondary text-center">
                No options available for this poll.
              </div>
            )}
          </div>

          <button
            onClick={handleVoteSubmit}
            className={`w-full mt-8 bg-[#6d29f6] text-white text-lg font-semibold px-4 py-3 rounded-lg shadow ${
              voteLoading ||
              isExpired(poll.expiry_datetime) ||
              poll.is_voted ||
              votedPolls.includes(poll.id)
                ? "opacity-100 cursor-not-allowed"
                : "hover:bg-[#6d29f6]"
            }`}
            disabled={
              voteLoading ||
              isExpired(poll.expiry_datetime) ||
              poll.is_voted ||
              votedPolls.includes(poll.id)
            }
          >
            {poll.is_voted || votedPolls.includes(poll.id)
              ? t("voted_already")
              : voteLoading
              ? t("submitting")
              : isExpired(poll.expiry_datetime)
              ? t("expired")
              : t("submit_vote")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VoteModal;
